/**
 * @deprecated Use useWhoopDataCached from '@/hooks/useWhoopDataCached' instead.
 * This hook is kept for backwards compatibility but useWhoopDataCached provides
 * better performance through React Query caching.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { generateWhoopDemoData } from '@/lib/whoopDemoData';

export interface WhoopDayData {
  id: string;
  date: string;
  recovery_score: number | null;
  hrv_score: number | null;
  resting_heart_rate: number | null;
  sleep_performance: number | null;
  sleep_duration_minutes: number | null;
  sleep_quality_score: number | null;
  strain_score: number | null;
  calories_burned: number | null;
  raw_data: any;
}

export interface WhoopStats {
  avgRecovery: number;
  avgHrv: number;
  avgSleep: number;
  avgStrain: number;
  totalDays: number;
  trend: 'improving' | 'declining' | 'stable';
}

export function useWhoopData(days: number = 30) {
  const [data, setData] = useState<WhoopDayData[]>([]);
  const [stats, setStats] = useState<WhoopStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'active' | 'expired' | 'disconnected'>('disconnected');

  const applyDemoData = useCallback((lastSyncValue: string | null = new Date().toISOString()) => {
    const demoData = generateWhoopDemoData(days) as WhoopDayData[];
    setData(demoData);
    setStats(calculateStats(demoData));
    setConnectionStatus('active');
    setIsConnected(true);
    setLastSync(lastSyncValue);
  }, [days]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        applyDemoData();
        return;
      }

      // Check connection status
      const { data: connection } = await (supabase as any)
        .from('whoop_connections')
        .select('is_active, last_sync_at, token_expires_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!connection) {
        applyDemoData();
        return;
      }

      const tokenExpired = connection.token_expires_at && new Date(connection.token_expires_at) < new Date();
      
      if (!connection.is_active || tokenExpired) {
        applyDemoData(connection.last_sync_at || new Date().toISOString());
        return;
      }

      setConnectionStatus('active');
      setIsConnected(true);
      setLastSync(connection.last_sync_at);

      // Fetch WHOOP data
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const startStr = startDate.toISOString().split('T')[0];

      const { data: whoopData, error } = await (supabase as any)
        .from('whoop_data')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startStr)
        .order('date', { ascending: false });

      if (error) {
        // Error fetching WHOOP data
        applyDemoData(connection.last_sync_at || new Date().toISOString());
        return;
      }

      if (!whoopData || whoopData.length === 0) {
        applyDemoData(connection.last_sync_at || new Date().toISOString());
        return;
      }

      setData(whoopData || []);

      // Calculate stats
      if (whoopData && whoopData.length > 0) {
        const validRecovery = whoopData.filter((d: WhoopDayData) => d.recovery_score != null);
        const validHrv = whoopData.filter((d: WhoopDayData) => d.hrv_score != null);
        const validSleep = whoopData.filter((d: WhoopDayData) => d.sleep_performance != null);
        const validStrain = whoopData.filter((d: WhoopDayData) => d.strain_score != null);

        const avgRecovery = validRecovery.length > 0 
          ? validRecovery.reduce((sum: number, d: WhoopDayData) => sum + (d.recovery_score || 0), 0) / validRecovery.length 
          : 0;
        const avgHrv = validHrv.length > 0 
          ? validHrv.reduce((sum: number, d: WhoopDayData) => sum + (d.hrv_score || 0), 0) / validHrv.length 
          : 0;
        const avgSleep = validSleep.length > 0 
          ? validSleep.reduce((sum: number, d: WhoopDayData) => sum + (d.sleep_performance || 0), 0) / validSleep.length 
          : 0;
        const avgStrain = validStrain.length > 0 
          ? validStrain.reduce((sum: number, d: WhoopDayData) => sum + (d.strain_score || 0), 0) / validStrain.length 
          : 0;

        // Calculate trend (compare first half vs second half)
        const midPoint = Math.floor(validRecovery.length / 2);
        if (midPoint > 0) {
          const recentAvg = validRecovery.slice(0, midPoint).reduce((sum: number, d: WhoopDayData) => sum + (d.recovery_score || 0), 0) / midPoint;
          const olderAvg = validRecovery.slice(midPoint).reduce((sum: number, d: WhoopDayData) => sum + (d.recovery_score || 0), 0) / (validRecovery.length - midPoint);
          const diff = recentAvg - olderAvg;
          
          let trend: 'improving' | 'declining' | 'stable' = 'stable';
          if (diff > 5) trend = 'improving';
          else if (diff < -5) trend = 'declining';

          setStats({
            avgRecovery,
            avgHrv,
            avgSleep,
            avgStrain,
            totalDays: whoopData.length,
            trend
          });
        } else {
          setStats({
            avgRecovery,
            avgHrv,
            avgSleep,
            avgStrain,
            totalDays: whoopData.length,
            trend: 'stable'
          });
        }
      }
    } catch (error) {
      // Error in useWhoopData
      applyDemoData();
    } finally {
      setIsLoading(false);
    }
  }, [days, applyDemoData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get today's data OR the most recent day with complete data (has sleep or strain)
  const getLatestCompleteData = (): WhoopDayData | null => {
    if (!data.length) return null;
    
    // First try today's data if it has sleep/strain
    const today = data[0];
    if (today && (today.sleep_performance != null || today.strain_score != null)) {
      return today;
    }
    
    // Otherwise find the most recent day with complete data
    const completeDay = data.find(d => d.sleep_performance != null || d.strain_score != null);
    return completeDay || today; // Fall back to today if no complete data found
  };

  return {
    data,
    stats,
    isLoading,
    isConnected,
    connectionStatus,
    lastSync,
    refresh: loadData,
    latestCompleteData: getLatestCompleteData()
  };
}

function calculateStats(data: WhoopDayData[]): WhoopStats {
  const validRecovery = data.filter((d: WhoopDayData) => d.recovery_score != null);
  const validHrv = data.filter((d: WhoopDayData) => d.hrv_score != null);
  const validSleep = data.filter((d: WhoopDayData) => d.sleep_performance != null);
  const validStrain = data.filter((d: WhoopDayData) => d.strain_score != null);

  const avgRecovery = validRecovery.length > 0 
    ? validRecovery.reduce((sum: number, d: WhoopDayData) => sum + (d.recovery_score || 0), 0) / validRecovery.length 
    : 0;
  const avgHrv = validHrv.length > 0 
    ? validHrv.reduce((sum: number, d: WhoopDayData) => sum + (d.hrv_score || 0), 0) / validHrv.length 
    : 0;
  const avgSleep = validSleep.length > 0 
    ? validSleep.reduce((sum: number, d: WhoopDayData) => sum + (d.sleep_performance || 0), 0) / validSleep.length 
    : 0;
  const avgStrain = validStrain.length > 0 
    ? validStrain.reduce((sum: number, d: WhoopDayData) => sum + (d.strain_score || 0), 0) / validStrain.length 
    : 0;

  const midPoint = Math.floor(validRecovery.length / 2);
  let trend: 'improving' | 'declining' | 'stable' = 'stable';
  if (midPoint > 0) {
    const recentAvg = validRecovery.slice(0, midPoint).reduce((sum: number, d: WhoopDayData) => sum + (d.recovery_score || 0), 0) / midPoint;
    const olderAvg = validRecovery.slice(midPoint).reduce((sum: number, d: WhoopDayData) => sum + (d.recovery_score || 0), 0) / (validRecovery.length - midPoint);
    const diff = recentAvg - olderAvg;
    if (diff > 5) trend = 'improving';
    else if (diff < -5) trend = 'declining';
  }

  return {
    avgRecovery,
    avgHrv,
    avgSleep,
    avgStrain,
    totalDays: data.length,
    trend,
  };
}

// Get today's or most recent data
export function useWhoopTodayData() {
  const [todayData, setTodayData] = useState<WhoopDayData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTodayData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await (supabase as any)
          .from('whoop_data')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          // Error fetching today data
          return;
        }

        setTodayData(data);
      } catch (error) {
        // Error in useWhoopTodayData
      } finally {
        setIsLoading(false);
      }
    };

    loadTodayData();
  }, []);

  return { todayData, isLoading };
}
